import dayjs, { OpUnitType } from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import { BlockMessage, RichBlockMessage } from '../types'
import { User } from '@botpress/webchat-client'
import { defaultTranslations, type WebchatTranslations } from '../translations'

dayjs.extend(isToday)
dayjs.extend(isYesterday)

export function enrichMessage(
  messages: BlockMessage[],
  participants: User[],
  currentUserId: string,
  botName?: string,
  botAvatar?: string
): RichBlockMessage[] {
  return messages.map((message) => {
    const { authorId } = message
    const direction = authorId === currentUserId ? 'outgoing' : 'incoming'

    const participant = participants.find((participant) => participant.id === authorId)

    let sender
    if (participant && participant.id === currentUserId) {
      sender = {
        name: participant.name || 'You',
        avatar: participant.pictureUrl,
      }
    } else if (participant) {
      sender = {
        name: participant.name || 'Agent',
        avatar: participant.pictureUrl,
      }
    } else {
      sender = {
        name: botName || 'Bot',
        avatar: botAvatar,
      }
    }

    return {
      ...message,
      direction,
      sender,
    }
  })
}

/**
 * @param interval - The interval to check for date differences (default is 'day')
 * @returns Array of message objects with system date messages inserted at every change of date more than the `interval`
 *  */
export function withDateSystemMessages(
  messages: RichBlockMessage[],
  translations: WebchatTranslations = defaultTranslations,
  interval: OpUnitType = 'day'
): RichBlockMessage[] {
  if (!messages || messages.length === 0) return []

  const result: RichBlockMessage[] = []
  if (messages[0]?.timestamp) {
    result.push(getDateSystemMessage(messages[0].timestamp, translations))
  }

  for (let i = 0; i < messages.length; i++) {
    if (messages[i]) {
      result.push(messages[i]!)
    }

    if (i + 1 < messages.length) {
      const currentDate = messages[i]?.timestamp
      const nextDate = messages[i + 1]?.timestamp

      if (!dayjs(currentDate).isSame(nextDate, interval) && nextDate) {
        result.push(getDateSystemMessage(nextDate, translations))
      }
    }
  }

  return result
}

function getDateSystemMessage(date: Date, translations: WebchatTranslations): RichBlockMessage {
  return {
    id: `system-date-${dayjs(date).format('YYYY-MM-DD')}`,
    direction: 'system',
    sender: { name: 'system' },
    timestamp: date,
    block: {
      type: 'text',
      text: humanizeTimestamp(date, translations),
    },
  }
}

export function humanizeTimestamp(
  timestamp: Date,
  translations: WebchatTranslations = defaultTranslations,
  long = false
): string {
  const date = dayjs(timestamp)
  const timeFormat = 'h:mma'
  const today = translations['date.today']
  const yesterday = translations['date.yesterday']

  if (date.isToday()) {
    return long ? `${today} • ${date.format(timeFormat)}` : today
  } else if (date.isYesterday()) {
    return long ? `${yesterday} • ${date.format(timeFormat)}` : yesterday
  } else {
    return long ? date.format(`YYYY-MM-DD • ${timeFormat}`) : date.format('YYYY-MM-DD')
  }
}
